import GeneralDialog from '../../../../../../shared/general-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  createSharedDriveFolder,
} from '../../../../../../api/users-api';
import { handleErrors } from '../../../../../../utils/errors';
import { useState } from 'react';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { useStore } from '../../../../../state/zustand-store';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import { Messages } from '../../../../../../utils/message-constants';
import ActingAsUserMessage from '../../../../../../shared/acting-as-user-message';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

interface SharedDrivesCreateFolderDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedFolder: string;
  changeLogRoles: string[];
}

export default function SharedDrivesCreateFolderDialog({
  open,
  handleClose,
  userEmail,
  selectedFolder,
  changeLogRoles,
}: SharedDrivesCreateFolderDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const [folderName, setFolderName] = useState('');
  const { setSnackbar } = useStore();

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      createSharedDriveFolder(
        selectedFolder,
        folderName.trim(),
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Folder'),
      });
      setChangeLogMessage('');

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveFolders, userEmail],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveFolders, userEmail, selectedFolder],
      });
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('the folder'),
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.content.create_drive_folder, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.content
            .create_drive_folder,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives.content
            .create_drive_folder,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setFolderName('');
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanCreateFolders.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
        setFolderName('');
      }}
      title="Create Folder"
      dialogContent={
        <Box sx={{ px: 4 }}>
          <ActingAsUserMessage userEmail={userEmail} />
          <TextField
            fullWidth
            autoFocus
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            autoComplete="off"
            label="Folder Name"
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Submit',
        action: handleSubmit,
        loading: isPending,
        disabled: folderName.trim() === '',
      }}
    />
  );
}
